import { useEffect, useState, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ProfileImage,
  PhoneNotifications,
  LanguagePreference,
  DesignatedRepresentative,
  DeleteProfile,
  Password,
} from 'templates';

import { Button, Headings } from '@sentara-health/optimahealth_web';

import { ProfileDetailsProps } from './interface';
import { DeviceTypes, useDeviceType } from 'common';
import { GlobalContext } from 'context';

function ProfileDetails({
  state,
  setState,
  getPhone,
  changePassword,
  setChangePassword,
}: Readonly<ProfileDetailsProps>) {
  const { t } = useTranslation();
  const NUMBER_OF_COLLAPSIBLE_COMPONENTS = 4;
  // Default collapse all elements
  const [expandedState, setExpandedState] = useState<Array<boolean>>(
    new Array(NUMBER_OF_COLLAPSIBLE_COMPONENTS).fill(false)
  );
  const { getFeature } = useContext(GlobalContext); // Access global feature flags
  const designatedRepFeature = getFeature?.DesignatedRepresentativeTab ?? false;
  const languagePreference = getFeature?.LanguagePreference ?? false;
  const emailConsent = getFeature?.EmailConsent ?? false;
  const textNotificationsFeature = getFeature?.TextNotifications ?? false;
  const communicationChannelFeature = getFeature?.CommunicationChannel ?? false;
  const [expandAll, setExpandAll] = useState<boolean>(false);
  const deviceType = useDeviceType();
  const isMobile = deviceType === DeviceTypes.MOBILE;

  useEffect(() => {
    const shouldExpandAll = expandedState.every((value) => value === true);
    setExpandAll(shouldExpandAll);
  }, [expandedState]);

  const expandCollapse = () => {
    if (expandAll) {
      setExpandedState(new Array(NUMBER_OF_COLLAPSIBLE_COMPONENTS).fill(false));
    } else {
      setExpandedState(new Array(NUMBER_OF_COLLAPSIBLE_COMPONENTS).fill(true));
    }
  };

  const onExpand = (index: number) => {
    setExpandedState((expandedState) => {
      const temp = [...expandedState];
      temp[index] = true;
      return temp;
    });
  };

  const onCollapse = (index: number) => {
    setExpandedState((expandedState) => {
      const temp = [...expandedState];
      temp[index] = false;
      return temp;
    });
  };

  return (
    <>
      {/* Renders the profile image component */}
      <ProfileImage
        state={state}
        setState={setState}
        getPhone={getPhone}
      />

      {isMobile ? (
        <div className="d-flex justify-content-start mb-4 profile_expand_collapse_btn">
          <Button
            type="button"
            dataTestId={!expandAll ? 'expandAll' : 'collapseAll'}
            variant='secondary_btn'
            onClick={expandCollapse}>
            {
              !expandAll
                ? t('profile.expandAll')
                : t('profile.collapseAll')
            }</Button>
        </div>
      ) : null}

      {/* Conditionally render PhoneNotifications based on feature flag */}
      {(emailConsent ||
        textNotificationsFeature ||
        communicationChannelFeature) ? (
        <div data-testid="phone-notifications-container">
          <PhoneNotifications
            key={`phoneNotifications-${expandAll ? 'expanded' : ''}`}
            smsConsent={state.smsConsent}
            setState={setState}
            state={state}
            expand={expandedState[1]}
            onExpand={() => onExpand(1)}
            onCollapse={() => onCollapse(1)}
          />
        </div>
      ) : null}

      {/* Renders the language preference component */}
      {languagePreference ? (
        <LanguagePreference
          key={`languagePreferences-${expandAll ? 'expanded' : ''} `}
          languagePreference={state.languagePreference ?? ''}
          expand={expandedState[2]}
          onExpand={() => onExpand(2)}
          onCollapse={() => onCollapse(2)}
          state={state}
          setState={setState}
        />
      ) : null}

      {/* Renders password component */}
      <Password
        key={`password-${expandAll ? 'expanded' : ''} `}
        state={state}
        setState={setState}
        changePassword={changePassword}
        setChangePassword={setChangePassword}
        expand={expandedState[3]}
        onExpand={() => onExpand(3)}
        onCollapse={() => onCollapse(3)}
      />


      {/* Conditionally render DesignatedRepresentative based on feature flag */}
      {designatedRepFeature ? (
        <DesignatedRepresentative
          key={`designatedRepresentative-${expandAll ? 'expanded' : ''} `}
          expand={expandedState[0]}
          onExpand={() => onExpand(0)}
          onCollapse={() => onCollapse(0)}
        />
      ) : null}

      {/* Renders the delete profile component */}
      <DeleteProfile />
    </>
  );
}

export default ProfileDetails;